import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Sparkles, ShieldCheck, Mail } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import RecruitEmailTemplates from "./RecruitEmailTemplates";

const PROVIDER_LABELS = {
    apollo: "Apollo (external sourcing)",
    juicebox: "Juicebox AI (external sourcing, coming later)",
    firecrawl: "Firecrawl (URL scraping)",
    resend: "Resend (email delivery)",
    affinda: "Affinda (resume parsing)",
};

export default function RecruitSettings() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [togglingNudge, setTogglingNudge] = useState(false);
    const reload = () =>
        api.get("/admin/recruit/settings")
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    useEffect(() => { reload(); }, []);

    const toggleNudge = async () => {
        if (!data?.verification_nudge) return;
        const nextPaused = !data.verification_nudge.paused;
        try {
            setTogglingNudge(true);
            const { data: res } = await api.post("/admin/recruit/settings/verify-nudge", { paused: nextPaused });
            setData((d) => ({ ...d, verification_nudge: { ...d.verification_nudge, ...res } }));
            toast.success(nextPaused ? "Verification nudge paused." : "Verification nudge resumed.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setTogglingNudge(false);
        }
    };
    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>;
    if (!data) return (
        <div data-testid="recruit-settings" className="py-12 text-center">
            <p className="text-sm text-muted-foreground">Couldn&apos;t load Recruit settings.</p>
            <button
                data-testid="recruit-settings-retry"
                onClick={() => { setLoading(true); reload(); }}
                className="mt-3 px-3 py-1.5 text-sm rounded-md border border-border hover:bg-muted transition"
            >
                Retry
            </button>
        </div>
    );

    return (
        <div data-testid="recruit-settings" className="space-y-4 max-w-3xl">
            <div>
                <h2 className="font-heading font-medium text-lg">Settings</h2>
                <p className="text-sm text-muted-foreground">Recruit configuration + provider health. Read-only for now — edit keys via backend .env.</p>
            </div>

            <div className="card-soft p-4" data-testid="settings-llm">
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-violet-500" />
                    <h3 className="font-heading font-medium">LLM Copilot</h3>
                </div>
                <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">Model:</span> <span className="font-mono">{data?.llm?.model || "—"}</span></div>
                    <div><span className="text-muted-foreground">Provider:</span> {data?.llm?.provider || "—"}</div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                    Set <code>LLM_MODEL</code> in <code>backend/.env</code> to swap without a code change.
                </p>
            </div>

            <div className="card-soft p-4" data-testid="settings-providers">
                <h3 className="font-heading font-medium mb-3">External providers</h3>
                <div className="divide-y divide-border">
                    {Object.entries(data?.providers || {}).map(([key, info]) => (
                        <div key={key} className="py-2 flex items-center justify-between gap-3" data-testid={`settings-provider-${key}`}>
                            <div className="min-w-0">
                                <div className="font-medium text-sm">{PROVIDER_LABELS[key] || key}</div>
                                <div className="text-xs text-muted-foreground font-mono">{info.masked || "not set"}</div>
                            </div>
                            {info.configured ? (
                                <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400"><CheckCircle2 size={10} className="mr-1" /> Connected</Badge>
                            ) : (
                                <Badge variant="outline" className="border-slate-400 text-muted-foreground"><XCircle size={10} className="mr-1" /> Not configured</Badge>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <RecruitEmailTemplates />

            <div className="card-soft p-4" data-testid="settings-verify-nudge">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck size={14} className="text-emerald-500" />
                    <h3 className="font-heading font-medium">Verification nudge</h3>
                    {data?.verification_nudge?.paused ? (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">Paused</Badge>
                    ) : (
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-400">Active</Badge>
                    )}
                </div>
                <p className="text-xs text-muted-foreground max-w-xl">
                    Daily 09:15 UTC. Emails talent who applied to a role in the last 7 days
                    and haven&apos;t verified their ID, one time only — tracked in the
                    <code className="mx-1">verification_nudges</code> collection.
                </p>
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                        <Mail size={12} /> <span className="font-medium text-foreground">{data?.verification_nudge?.total_sent ?? 0}</span> emails sent lifetime
                    </div>
                    {data?.verification_nudge?.updated_at && (
                        <div className="text-xs text-muted-foreground">
                            Last change: <span className="font-mono">{new Date(data.verification_nudge.updated_at).toLocaleString()}</span>
                            {data.verification_nudge.updated_by ? ` · ${data.verification_nudge.updated_by}` : ""}
                        </div>
                    )}
                    <button
                        onClick={toggleNudge}
                        disabled={togglingNudge}
                        data-testid="settings-verify-nudge-toggle"
                        className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition disabled:opacity-60 ${
                            data?.verification_nudge?.paused
                                ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                                : "bg-background text-amber-700 border-amber-500/40 hover:bg-amber-500/10"
                        }`}
                    >
                        {togglingNudge
                            ? "Saving…"
                            : data?.verification_nudge?.paused ? "Resume nudge" : "Pause nudge"}
                    </button>
                </div>
            </div>

            <div className="card-soft p-4 border-dashed" data-testid="settings-security">
                <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">Access control</div>
                <p className="text-sm">{data?.role_gating || "—"}</p>
            </div>
        </div>
    );
}
